import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Modal, Form, Row, Col, Breadcrumb, message, DatePicker } from "antd";
import { Update_User, Get_City, Get_States, List_Gender_From_Role, List_Employement_Type_From_Role, Current_Designation, Verify_Employee_Id, Role_Account_Query, Get_User_Types, GET_MANAGERS_FOR_ROLE } from "../../../../../services/userAPI";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import SelectDropdown from "react-select";
import TextInputField from "../../../../../reusableField/TextInputField2";
import { validationRegexAlphaNumeric, validationRegexOnlyAlpha, validationRegexMobileNo } from "../../../../../regex/regex";
import moment from "moment";
import dayjs from "dayjs";
import "./editUserModal.css";
import { Margin } from "@mui/icons-material";

const EditUserModal = ({ visible, onCancel, userData, roleId, accountId, refetch }) => {
    const [formEdit] = Form.useForm();
    const [editFormData, setEditFormData] = useState({
        employeeid: "",
        employeeMailId: "",
        firstName: "",
        lastName: "",
        gender: null,
        dateOfBirth: null,
        phoneNumber: "",
        previousExperience: "",
        higestEducationQualification: "",
        previousEmployer: "",
        employeeType: null,
        designation: null,
        joiningDate: null,
        branch: "",
        state: null,
        city: null,
        zone: null,
        employeeIds: "",
        reportingDate: null,
        userType: null,
    });
    const [touched, setTouched] = useState({});
    const [errors, setErrors] = useState({});
    const [step, setStep] = useState(0);
    const [selectedState, setSelectedState] = useState(null);
    const [cityOptions, setCityOptions] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedDesignation, setSelectedDesignation] = useState(null);
    const [managerName, setManagerName] = useState("No Manager");
    const [selectedManagerEmployeeId, setSelectedManagerEmployeeId] = useState("");
    const [managerSearchTerm, setManagerSearchTerm] = useState("");
    const [genderOptions, setGenderOptions] = useState([]);
    const [employmentOptions, setEmploymentOptions] = useState([]);
    const [designationOptions, setDesignationOptions] = useState([]);
    const [isLoadingCities, setIsLoadingCities] = useState(false);
    const [isLoadingManager, setIsLoadingManager] = useState(false);

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const [updateUser, { loading: updateUserLoading }] = useMutation(Update_User);
    const { data: stateData } = useQuery(Get_States);
    const stateOptions = stateData?.getStates?.map(state => ({ value: state.id, label: state.stateName })) || [];
    const [fetchCities] = useLazyQuery(Get_City, {
        fetchPolicy: "cache-and-network",
        onCompleted: (data) => {
            setCityOptions(data?.getCity?.map(city => ({ value: city.id, label: city.cityName })) || []);
            setIsLoadingCities(false);
        },
        onError: () => setIsLoadingCities(false),
    });
    const { data: managersListData, loading: managersListLoading } = useQuery(GET_MANAGERS_FOR_ROLE, {
        variables: {
            roleId: parseInt(roleId) || 0,
            searchTerm: managerSearchTerm || "",
            pagingCriteria: {
                limit: 50000,
                cursor: null,
                sortByField: "name",
                sortOrder: "asc",
            },
        },
        skip: !roleId,
        fetchPolicy: "network-only",
    });

    const managerOptions = useMemo(() => {
        if (!managersListData?.getManagersForRole?.data) return [];
        return managersListData.getManagersForRole.data.map((manager) => ({
            value: manager.id,
            label: `${manager.name} (${manager.employeeId})`,
            name: manager.name,
            employeeId: manager.employeeId,
        }));
    }, [managersListData]);

    const selectedManagerValue = useMemo(() => {
        if (!selectedManagerEmployeeId) return null;

        const matchedOption = managerOptions.find(option =>
            String(option.employeeId) === String(selectedManagerEmployeeId)
        );

        if (matchedOption) return matchedOption;

        if (managerName) {
            return {
                value: `placeholder-${selectedManagerEmployeeId}`,
                label: `${managerName} (${selectedManagerEmployeeId})`,
                name: managerName,
                employeeId: selectedManagerEmployeeId,
            };
        }

        return {
            value: `placeholder-${selectedManagerEmployeeId}`,
            label: selectedManagerEmployeeId,
            name: "Selected Manager",
            employeeId: selectedManagerEmployeeId,
        };
    }, [selectedManagerEmployeeId, managerOptions, managerName]);

    const handleManagerSelect = (selectedOption) => {
        if (selectedOption) {
            setSelectedManagerEmployeeId(selectedOption.employeeId || "");
            setManagerName(selectedOption.name || "No Manager");
            setEditFormData(prev => ({ ...prev, employeeIds: selectedOption.employeeId || "" }));
            setTouched(prev => ({ ...prev, employeeIds: true }));
            setErrors(prev => ({ ...prev, employeeIds: undefined }));
            formEdit.setFieldsValue({ managerSelection: selectedOption.value });
            formEdit.validateFields(["reportingDate"]);
        } else {
            setSelectedManagerEmployeeId("");
            setManagerName("No Manager");
            setEditFormData(prev => ({ ...prev, employeeIds: "", reportingDate: null }));
            formEdit.setFieldsValue({ managerSelection: null, reportingDate: null });
        }
    };

    const { data: genderData } = useQuery(List_Gender_From_Role, { variables: { jobroleId: roleId } });
    const { data: employmentData } = useQuery(List_Employement_Type_From_Role, { variables: { jobRoleId: roleId } });
    const { data: designationsData } = useQuery(Current_Designation, { variables: { jobroleId: roleId } });

    const { data: jobRoleData, loading: jobRoleLoading, error: jobRoleError } = useQuery(Role_Account_Query, {
        variables: { id: roleId, accountId: accountId },
        skip: !roleId, // Skip query if roleId is not available
        fetchPolicy: "network-only",
    });

    const [userTypesOptions, setUserTypesOptions] = useState([]);
    const { data: userTypesData, loading: userTypesLoading } = useQuery(Get_User_Types, {
        fetchPolicy: "cache-and-network",
    });

    useEffect(() => {
        if (userTypesData?.getUserTypes) {
            setUserTypesOptions(
                userTypesData.getUserTypes.map(type => ({
                    value: type.id,
                    label: type.userType,
                }))
            );
        }
    }, [userTypesData]);

    const [loginConfig, setLoginConfig] = useState({
        numberLogin: false,
        emailLogin: false,
    });

    useEffect(() => {
        if (jobRoleData?.getAllJobrole?.[0]) {
            setLoginConfig({
                numberLogin: jobRoleData.getAllJobrole[0].numberLogin,
                emailLogin: jobRoleData.getAllJobrole[0].emailLogin,
            });
        }
    }, [jobRoleData]);

    useEffect(() => {
        setGenderOptions(genderData?.listGenderFromRole?.map(gender => ({ value: gender.id, label: gender.name })) || []);
        setEmploymentOptions(employmentData?.listEmployementTypeFromRole?.map(type => ({ value: type.id, label: type.name })) || []);
        setDesignationOptions(designationsData?.getDesignationsJobroleid?.map(type => ({ value: type.id, label: type.designation })) || []);
    }, [genderData, employmentData, designationsData]);

    const debouncedFetchManager = useCallback(
        debounce((employeeId) => {
            if (employeeId && employeeId.length >= 3) {
                setIsLoadingManager(true);
                // fetchManager({ variables: { employeeId } });
            }
        }, 500),
        []
    );

    function debounce(func, delay) {
        let timeoutId;
        return function (...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(this, args), delay);
        };
    }

    const isInitialMount = useRef(true);

    useEffect(() => {
        if (!visible) {
            setStep(0);
            setEditFormData({
                employeeid: "",
                employeeMailId: "",
                firstName: "",
                lastName: "",
                gender: null,
                dateOfBirth: null,
                phoneNumber: "",
                previousExperience: "",
                higestEducationQualification: "",
                previousEmployer: "",
                employeeType: null,
                designation: null,
                joiningDate: null,
                branch: "",
                state: null,
                city: null,
                zone: null,
                employeeIds: "",
                reportingDate: null,
                userType: null,
            });
            setTouched({});
            setErrors({});
            setSelectedState(null);
            setSelectedCity(null);
            setSelectedDesignation(null);
            setCityOptions([]);
            setManagerName("No Manager");
            setSelectedManagerEmployeeId("");
            setManagerSearchTerm("");
            formEdit.resetFields();
            isInitialMount.current = true;
        }
    }, [visible, formEdit]);

    useEffect(() => {
        if (visible && userData && genderOptions.length > 0 && employmentOptions.length > 0 && designationOptions.length > 0 && userTypesOptions.length > 0 && isInitialMount.current) {
            const zoneOptions = [
                { label: "North", value: "north" },
                { label: "South", value: "south" },
                { label: "East", value: "east" },
                { label: "West", value: "west" },
            ];
            const initialZone = zoneOptions.find(opt => opt.value === userData.zone?.toLowerCase()) || null;

            let initialUserType = null;
            if (userData.userType) {
                if (typeof userData.userType === "string") {
                    initialUserType = userTypesOptions.find(opt =>
                        opt.label.toLowerCase() === userData.userType.toLowerCase()
                    ) || null;
                } else if (typeof userData.userType === "number" || (userData.userType && userData.userType.id)) {
                    const userTypeId = typeof userData.userType === "number" ? userData.userType : userData.userType.id;
                    initialUserType = userTypesOptions.find(opt => opt.value === userTypeId) || null;
                }

                if (!initialUserType && userData.userTypeId) {
                    initialUserType = userTypesOptions.find(opt => opt.value === userData.userTypeId) || null;
                }
            }

            const initialData = {
                employeeid: userData.employeeId?.toString() || "",
                employeeMailId: userData.email || "",
                firstName: userData.name || "",
                lastName: userData.surname || "",
                gender: genderOptions.find(opt => opt.value === userData.genderId) || null,
                dateOfBirth: userData.dateOfBirth ? dayjs(userData.dateOfBirth) : null,
                phoneNumber: userData.phoneNumber || "",
                previousExperience: userData.previousExperience?.toString() || "",
                higestEducationQualification: userData.higestEducationQualification || "",
                previousEmployer: userData.previousEmployer || "",
                employeeType: employmentOptions.find(opt => opt.value === userData.employeeTypeId) || null,
                designation: designationOptions.find(opt => opt.value === userData.designationId) || null,
                joiningDate: userData.joiningDate ? dayjs(userData.joiningDate) : null,
                branch: userData.branch || "",
                state: stateOptions.find(opt => opt.value === userData.state) || null,
                city: { value: userData.city, label: userData.cityName } || null,
                zone: initialZone,
                employeeIds: userData.reporting?.employeeId || "",
                reportingDate: userData.reportingDate ? dayjs(userData.reportingDate) : null,
                userType: initialUserType,
            };

            setEditFormData(initialData);

            setSelectedState(stateOptions.find(opt => opt.value === userData.state) || null);
            setSelectedCity(userData.city ? { value: userData.city, label: userData.cityName } : null);
            setSelectedDesignation(designationOptions.find(opt => opt.value === userData.designationId) || null);

            formEdit.setFieldsValue({
                ...initialData,
                dateOfBirth: initialData.dateOfBirth,
                joiningDate: initialData.joiningDate,
                reportingDate: initialData.reportingDate,
                userType: initialUserType,
            });

            if (userData.state) {
                setIsLoadingCities(true);
                fetchCities({ variables: { stateId: userData.state } });
            }

            if (userData.reporting?.employeeId) {
                setSelectedManagerEmployeeId(userData.reporting.employeeId);
                setManagerName(userData.reporting.name || userData.employeeName || "Manager");
            }

            isInitialMount.current = false;
        }
    }, [visible, userData, genderOptions, employmentOptions, userTypesOptions, designationOptions, stateOptions, formEdit, fetchCities]);

    useEffect(() => {
        if (visible && selectedManagerEmployeeId && managerOptions.length > 0) {
            // compare as strings to avoid type mismatch (number vs string)
            const matchingManager = managerOptions.find(option => String(option.employeeId) === String(selectedManagerEmployeeId));
            if (matchingManager) {
                // Use the same handler to ensure form state and editFormData are updated consistently
                handleManagerSelect(matchingManager);
                formEdit.setFieldsValue({ managerSelection: matchingManager });
            } else {
                // If no exact manager option found (maybe still loading), set a placeholder value so the select shows something
                const placeholder = selectedManagerEmployeeId
                    ? { value: `placeholder-${selectedManagerEmployeeId}`, label: `${managerName} (${selectedManagerEmployeeId})`, employeeId: selectedManagerEmployeeId, name: managerName }
                    : null;
                formEdit.setFieldsValue({ managerSelection: placeholder });
            }
        }
    }, [visible, selectedManagerEmployeeId, managerOptions, formEdit]);

    const handleInputChange = (e, field) => {
        const { name, value } = e.target || e;
        const fieldName = field || name;
        const updatedValue = fieldName === "previousExperience" && value ? parseInt(value, 10) || "" : value;

        setEditFormData(prev => ({ ...prev, [fieldName]: updatedValue }));
        setTouched(prev => ({ ...prev, [fieldName]: true }));

        if (fieldName === "employeeMailId") {
            if (loginConfig.emailLogin && !loginConfig.numberLogin && !value?.trim()) {
                setErrors((prev) => ({ ...prev, [fieldName]: "Employee Mail ID is required" }));
            } else if (value && !emailRegex.test(value)) {
                setErrors((prev) => ({ ...prev, [fieldName]: "Invalid email format" }));
            } else {
                setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
            }
        } else if (fieldName === "phoneNumber") {
            if (loginConfig.numberLogin && !loginConfig.emailLogin && !value?.trim()) {
                // setErrors((prev) => ({ ...prev, [fieldName]: "Phone Number is required" }));
            } else {
                setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
            }
        } else {
            setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
        }

        formEdit.setFieldsValue({ [fieldName]: updatedValue });
    };

    const handleSelectChange = (selectedOption, fieldName) => {
        setEditFormData(prev => ({ ...prev, [fieldName]: selectedOption }));
        setTouched(prev => ({ ...prev, [fieldName]: true }));
        setErrors(prev => ({ ...prev, [fieldName]: undefined }));
        formEdit.setFieldsValue({ [fieldName]: selectedOption });
    };

    const handleDateChange = (name, date) => {
        const dayjsDate = date ? dayjs(date) : null;
        setEditFormData(prev => ({ ...prev, [name]: dayjsDate }));
        setTouched(prev => ({ ...prev, [name]: true }));
        if (dayjsDate) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
        formEdit.setFieldsValue({ [name]: dayjsDate });
    };

    const handleStateChange = (selectedOption) => {
        setSelectedState(selectedOption);
        setSelectedCity(null);
        setCityOptions([]);
        setEditFormData(prev => ({ ...prev, state: selectedOption, city: null }));
        setTouched(prev => ({ ...prev, state: true, city: true }));
        setErrors(prev => ({ ...prev, state: undefined, city: undefined }));
        if (selectedOption?.value) {
            setIsLoadingCities(true);
            fetchCities({ variables: { stateId: selectedOption.value } });
        }
        formEdit.setFieldsValue({ state: selectedOption, city: null });
    };

    const handleCityChange = (selectedOption) => {
        setSelectedCity(selectedOption);
        setEditFormData(prev => ({ ...prev, city: selectedOption }));
        setTouched(prev => ({ ...prev, city: true }));
        setErrors(prev => ({ ...prev, city: undefined }));
        formEdit.setFieldsValue({ city: selectedOption });
    };

    const handleDesignationChange = (selectedOption) => {
        setSelectedDesignation(selectedOption);
        setEditFormData(prev => ({ ...prev, designation: selectedOption }));
        setTouched(prev => ({ ...prev, designation: true }));
        setErrors(prev => ({ ...prev, designation: undefined }));
        formEdit.setFieldsValue({ designation: selectedOption });
    };

    const handleZoneChange = (selectedOption) => {
        setEditFormData(prev => ({ ...prev, zone: selectedOption }));
        setTouched(prev => ({ ...prev, zone: true }));
        setErrors(prev => ({ ...prev, zone: undefined }));
        formEdit.setFieldsValue({ zone: selectedOption });
    };

    const handleNext = async () => {
        const newTouched = {
            employeeid: true,
            firstName: true,
            lastName: true,
            gender: true,
            dateOfBirth: true,
        };

        if (loginConfig.emailLogin && !loginConfig.numberLogin) {
            newTouched.employeeMailId = true;
        }
        if (loginConfig.numberLogin && !loginConfig.emailLogin) {
            newTouched.phoneNumber = true;
        }
        setTouched(prev => ({ ...prev, ...newTouched }));

        const stepErrors = validateFirstStep(editFormData);
        setErrors(prev => ({ ...prev, ...stepErrors }));

        if (Object.keys(stepErrors).length === 0) {
            try {
                const fieldsToValidate = ["employeeid", "firstName", "lastName", "gender", "dateOfBirth"];
                if (loginConfig.emailLogin && !loginConfig.numberLogin) {
                    fieldsToValidate.push("employeeMailId");
                }
                if (loginConfig.numberLogin && !loginConfig.emailLogin) {
                    fieldsToValidate.push("phoneNumber");
                }
                await formEdit.validateFields(fieldsToValidate);
                setStep(1);
            } catch (error) {
                console.error("Validation failed:", error);
                message.error("Please fill all required fields correctly before proceeding.");
            }
        }
    };

    const validateFirstStep = (data) => {
        const errors = {};
        const employeeId = data.employeeid?.toString() || "";
        if (!employeeId.trim()) errors.employeeid = "";
        if (loginConfig.emailLogin && !loginConfig.numberLogin) {
            if (!data.employeeMailId?.trim()) {
                errors.employeeMailId = "Employee Mail ID is required";
            } else if (!emailRegex.test(data.employeeMailId)) {
                errors.employeeMailId = "Invalid email format";
            }
        }
        if (loginConfig.numberLogin && !loginConfig.emailLogin) {
            if (!data.phoneNumber?.trim()) {
                // errors.phoneNumber = "Phone Number is required";
            } else if (!validationRegexMobileNo.test(data.phoneNumber)) {
                errors.phoneNumber = "Invalid phone number format";
            }
        }
        if (!data.firstName?.trim()) errors.firstName = "";
        if (!data.lastName?.trim()) errors.lastName = "";
        if (!data.dateOfBirth) errors.dateOfBirth = "";
        if (!data.gender) errors.gender = "";
        return errors;
    };

    const validateSecondStep = (data) => {
        const errors = {};
        if (!data.employeeType) errors.employeeType = "Employment type is required";
        if (!data.joiningDate) errors.joiningDate = "Date of joining is required";
        if (!data.designation) errors.designation = "Designation is required";
        if (!data.branch?.trim()) errors.branch = "Branch is required";
        if (!data.state) errors.state = "State is required";
        if (!data.city) errors.city = "City is required";
        if (!data.zone) errors.zone = "Zone is required";
        if (!data.userType) errors.userType = "Login Type is required";
        return errors;
    };

    const handleSubmit = async () => {
        const formatDate = (dateValue) => {
            if (!dateValue) return null;
            return dayjs.isDayjs(dateValue) ? dateValue.format("YYYY-MM-DD") : dayjs(dateValue).format("YYYY-MM-DD");
        };

        const userDataToUpdate = {
            accountId,
            currentRoleId: roleId,
            employeeId: editFormData.employeeid,
            firstName: editFormData.firstName,
            lastName: editFormData.lastName,
            email: editFormData.employeeMailId,
            phoneNumber: editFormData.phoneNumber,
            genderId: editFormData.gender?.value,
            employeeTypeId: editFormData.employeeType?.value,
            dateOfBirth: formatDate(editFormData.dateOfBirth),
            previousExperience: parseInt(editFormData.previousExperience, 10) || null,
            previousEmployer: editFormData.previousEmployer,
            higestEducationQualification: editFormData.higestEducationQualification,
            designation: editFormData.designation?.value,
            joiningDate: formatDate(editFormData.joiningDate),
            validFrom: formatDate(editFormData.reportingDate),
            branch: editFormData.branch,
            managerEmployeeId: editFormData.employeeIds || null,
            city: editFormData.city?.value,
            state: selectedState?.value,
            zone: editFormData.zone?.value || "",
            userId: userData?.id,
            userTypeId: editFormData.userType?.value || null,
        };

        try {
            const response = await updateUser({ variables: { data: [userDataToUpdate] } });
            if (response.data.updateMultipleUsers.success) {
                message.success("User updated successfully!");
                onCancel();
                refetch();
            } else {
                response.data.updateMultipleUsers.data.forEach(error => {
                    if (error?.message) message.error(error.message);
                });
            }
        } catch (error) {
            console.error("Update error:", error);
            message.error(error.message);
        }
    };

    const handleSave = async () => {
        const newTouched = {
            employeeType: true,
            joiningDate: true,
            designation: true,
            branch: true,
            state: true,
            city: true,
            zone: true,
            userType: true,
        };
        setTouched(prev => ({ ...prev, ...newTouched }));

        const stepErrors = validateSecondStep(editFormData);
        setErrors(prev => ({ ...prev, ...stepErrors }));

        if (Object.keys(stepErrors).length === 0) {
            try {
                await formEdit.validateFields(["employeeType", "joiningDate", "designation", "branch", "state", "city", "zone", "userType", "reportingDate"]);
                await handleSubmit();
            } catch (error) {
                console.error("Validation failed:", error);
            }
        }
    };

    const handleCancel = () => {
        setEditFormData({
            employeeid: "",
            employeeMailId: "",
            firstName: "",
            lastName: "",
            gender: null,
            dateOfBirth: null,
            phoneNumber: "",
            previousExperience: "",
            higestEducationQualification: "",
            previousEmployer: "",
            employeeType: null,
            designation: null,
            joiningDate: null,
            branch: "",
            state: null,
            city: null,
            zone: null,
            employeeIds: "",
            reportingDate: null,
            userType: null,
        });
        setTouched({});
        setErrors({});
        setStep(0);
        setSelectedState(null);
        setSelectedCity(null);
        setSelectedDesignation(null);
        setCityOptions([]);
        setManagerName("No Manager");
        setSelectedManagerEmployeeId("");
        setManagerSearchTerm("");
        formEdit.resetFields();
        isInitialMount.current = true;
        onCancel();
    };

    return (
        <Modal
            title="Edit Registration Form"
            className="edit-user-modal"
            open={visible}
            onCancel={handleCancel}
            footer={null}
            // width={580}
            centered
            destroyOnClose={true}
        >
            <div class="ant-row"></div>
            <Breadcrumb separator=">">
                <Breadcrumb.Item>
                    <a
                        style={{
                            fontWeight: step === 0 ? "bold" : "normal",
                            color: step === 0 ? "#d32f2f" : "rgb(188, 188, 188)",
                        }}
                        onClick={() => setStep(0)}
                    >
                        1. User Basic Data
                    </a>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <a
                        // onClick={() => setStep(1)}
                        style={{
                            fontWeight: step === 1 ? "bold" : "normal",
                            color: step === 1 ? "#d32f2f" : "rgb(188, 188, 188)",
                        }}
                    >
                        2. User Work Data
                    </a>
                </Breadcrumb.Item>
            </Breadcrumb>

            <Form className="edit-user-form" form={formEdit} layout="vertical" onSubmit={(e) => e.preventDefault()}>
                {step === 0 ? (
                    <>
                        <Row gutter={[16, 16]} style={{ marginBottom: "10px" }}>
                            <Col xs={24} sm={12} md={12} lg={12}>
                                <label className="required-label">Employee ID</label>
                                <Form.Item name="employeeid" rules={[{ required: true, message: "Employee ID is required!" }]}>
                                    <TextInputField
                                        name="employeeid"
                                        value={editFormData.employeeid}
                                        placeholder="Employee ID"
                                        feedbackMessage="Please input the Employee ID."
                                        validationRegex={validationRegexAlphaNumeric}
                                        onChange={handleInputChange}
                                    />
                                    {/* {touched.employeeid && errors.employeeid && <div className="createClientErrors">{errors.employeeid}</div>} */}
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={12} lg={12}>
                                <label className={loginConfig.emailLogin && !loginConfig.numberLogin ? "required-label" : ""}>
                                    Employee Mail ID{loginConfig.emailLogin && !loginConfig.numberLogin}
                                </label>
                                <Form.Item
                                    name="employeeMailId"
                                    rules={[
                                        {
                                            required: loginConfig.emailLogin && !loginConfig.numberLogin,
                                            message: "Employee Mail ID is required!",
                                        },
                                    ]}
                                >
                                    <TextInputField
                                        name="employeeMailId"
                                        value={editFormData.employeeMailId}
                                        placeholder="Employee Mail ID"
                                        feedbackMessage="Please enter a valid email address."
                                        onChange={handleInputChange}
                                    />
                                    {/* {touched.employeeMailId && errors.employeeMailId && <div className="createClientErrors">{errors.employeeMailId}</div>} */}
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]} style={{ marginBottom: "10px" }}>
                            <Col xs={24} sm={12} md={12} lg={12}>
                                <label className="required-label">First Name</label>
                                <Form.Item name="firstName" rules={[{ required: true, message: "First name is required!" }, { pattern: validationRegexOnlyAlpha, message: "Only alphabets allowed!" }]}>
                                    <TextInputField
                                        name="firstName"
                                        value={editFormData.firstName}
                                        placeholder="First Name"
                                        feedbackMessage="Please enter your first name."
                                        onChange={handleInputChange}
                                    />
                                    {/* {touched.firstName && errors.firstName && <div className="createClientErrors">{errors.firstName}</div>} */}
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={12} lg={12}>
                                <label className="required-label">Last Name</label>
                                <Form.Item name="lastName" rules={[{ required: true, message: "Last name is required!" }, { pattern: validationRegexOnlyAlpha, message: "Only alphabets allowed!" }]}>
                                    <TextInputField
                                        name="lastName"
                                        value={editFormData.lastName}
                                        placeholder="Last Name"
                                        feedbackMessage="Please enter your last name."
                                        onChange={handleInputChange}
                                    />
                                    {/* {touched.lastName && errors.lastName && <div className="createClientErrors">{errors.lastName}</div>} */}
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]} style={{ marginBottom: "10px" }}>
                            <Col xs={24} sm={12} md={12} lg={12}>
                                <label className="required-label">Gender</label>
                                <Form.Item name="gender" rules={[{ required: true, message: "Gender is required!" }]}>
                                    <div className="uniform-gender-field">
                                        <SelectDropdown
                                            classNamePrefix="edit-user-select"
                                            options={genderOptions}
                                            value={editFormData.gender}
                                            onChange={(selectedOption) => handleSelectChange(selectedOption, "gender")}
                                            placeholder="Select Gender"
                                            isSearchable
                                        />
                                    </div>
                                    {/* {touched.gender && errors.gender && <div className="createClientErrors">{errors.gender}</div>} */}
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={12} lg={12}>
                                <label className="required-label">Date of Birth</label>
                                <Form.Item name="dateOfBirth" rules={[{ required: true, message: "Date of birth is required!" }]}>
                                    <div>
                                        <DatePicker
                                            placeholder="Date of Birth"
                                            value={editFormData.dateOfBirth}
                                            onChange={(date) => handleDateChange("dateOfBirth", date)}
                                            format="YYYY-MM-DD"
                                            disabledDate={(current) => {
                                                return current && current > dayjs().subtract(18, "year").endOf("day");
                                            }}
                                        />
                                    </div>
                                    {/* {touched.dateOfBirth && errors.dateOfBirth && <div className="createClientErrors">{errors.dateOfBirth}</div>} */}
                                </Form.Item>
                            </Col>
                        </Row>

                        <div className="editUserWorkData"   >
                            <Row gutter={[16, 16]} style={{ marginBottom: "10px" }}>



                                <Col xs={24} sm={12} md={12} lg={12}>
                                    <label>Highest Education Qualification</label>
                                    <Form.Item name="higestEducationQualification">
                                        <TextInputField
                                            name="higestEducationQualification"
                                            value={editFormData.higestEducationQualification}
                                            placeholder="Highest Education Qualification"
                                            feedbackMessage="Please input the qualification."
                                            onChange={handleInputChange}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={12} lg={12}>
                                    <label>Previous Employer</label>
                                    <Form.Item name="previousEmployer">
                                        <TextInputField
                                            name="previousEmployer"
                                            value={editFormData.previousEmployer}
                                            placeholder="Previous Employer"
                                            feedbackMessage="Please input the employer."
                                            validationRegex={validationRegexAlphaNumeric}
                                            onChange={handleInputChange}
                                        />
                                    </Form.Item>
                                </Col>

                            </Row>

                            <Row gutter={[16, 16]} style={{ marginBottom: "10px" }}>


                                <Col xs={24} sm={12} md={12} lg={12}>
                                    <label>Previous Experience (Months)</label>
                                    <Form.Item name="previousExperience">
                                        <TextInputField
                                            name="previousExperience"
                                            value={editFormData.previousExperience}
                                            placeholder="Previous Experience (Months)"
                                            feedbackMessage="Please input the experience."
                                            onChange={(e) => handleInputChange(e, "previousExperience")}
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={12} lg={12}>
                                    <label className={loginConfig.numberLogin && !loginConfig.emailLogin ? "required-label" : ""}>
                                        Phone Number{loginConfig.numberLogin && !loginConfig.emailLogin}
                                    </label>

                                    <TextInputField
                                        name="phoneNumber"
                                        value={editFormData.phoneNumber}
                                        placeholder="Phone Number"
                                        maxLength={10}
                                        onChange={(value) => {
                                            const phoneValue = value?.target
                                                ? value.target.value
                                                : value || "";

                                            // Only numbers
                                            if (!/^\d*$/.test(phoneValue)) {
                                                return;
                                            }

                                            // First number should be only 6, 7, 8 or 9
                                            if (
                                                phoneValue.length === 1 &&
                                                !["6", "7", "8", "9"].includes(phoneValue)
                                            ) {
                                                return;
                                            }

                                            // Maximum 10 digits
                                            if (phoneValue.length > 10) {
                                                return;
                                            }

                                            // Update the value
                                            handleInputChange({
                                                target: {
                                                    name: "phoneNumber",
                                                    value: phoneValue,
                                                },
                                            });
                                        }}
                                    />

                                    {touched.phoneNumber && errors.phoneNumber && (
                                        <div className="createClientErrors">
                                            {errors.phoneNumber}
                                        </div>
                                    )}
                                </Col>


                            </Row>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px", gap: "10px" }}>
                            <button className="user-management-cancel-button-step1" onClick={handleCancel}>Cancel</button>
                            <button className="nextButton" onClick={handleNext}>Next</button>
                        </div>
                    </>
                ) : (
                    <>
                        <Row gutter={[16, 16]}>
                            <Col sm={12} xs={23} md={11} lg={12}>
                                <label className="required-label">Employment Type</label>
                                <Form.Item name="employeeType" rules={[{ required: true, message: "Employment type is required!" }]}>
                                    <SelectDropdown
                                        options={employmentOptions}
                                        value={editFormData.employeeType}
                                        onChange={(selectedOption) => handleSelectChange(selectedOption, "employeeType")}
                                        placeholder="Select Employment Type"
                                        isSearchable
                                    />
                                    {/* {touched.employeeType && errors.employeeType && <div className="createClientErrors">{errors.employeeType}</div>} */}
                                </Form.Item>
                            </Col>
                            <Col sm={12} xs={23} md={11} lg={12}>
                                <label className="required-label">Date of Joining</label>

                                <Form.Item
                                    name="joiningDate"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Date of joining is required!",
                                        },
                                    ]}
                                >
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        placeholder="Date of Joining"
                                        value={editFormData.joiningDate}
                                        onChange={(date) => handleDateChange("joiningDate", date)}
                                        format="YYYY-MM-DD"
                                        disabledDate={(current) => {
                                            return current && current.isAfter(dayjs(), "day");
                                        }}
                                    />

                                    {/* {touched.joiningDate && errors.joiningDate && (
                                        <div className="createClientErrors">
                                            {errors.joiningDate}
                                        </div>
                                    )} */}
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col sm={12} xs={23} md={11} lg={12}>
                                <label className="required-label">Current Designation</label>
                                <Form.Item name="designation" rules={[{ required: true, message: "Designation is required!" }]}>
                                    <SelectDropdown
                                        options={designationOptions}
                                        value={selectedDesignation}
                                        onChange={handleDesignationChange}
                                        placeholder="Current Designation"
                                        isSearchable
                                    />
                                    {/* {touched.designation && errors.designation && <div className="createClientErrors">{errors.designation}</div>} */}
                                </Form.Item>
                            </Col>
                            <Col sm={12} xs={23} md={11} lg={12}>
                                <label className="required-label">Current Branch</label>
                                <Form.Item name="branch" rules={[{ required: true, message: "Branch is required!" }, { pattern: validationRegexOnlyAlpha, message: "Only alphabets allowed!" }]}>
                                    <TextInputField
                                        name="branch"
                                        value={editFormData.branch}
                                        placeholder="Current Branch"
                                        feedbackMessage="Please input the branch."
                                        onChange={(e) => handleInputChange(e, "branch")}
                                    />
                                    {/* {touched.branch && errors.branch && <div className="createClientErrors">{errors.branch}</div>} */}
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col sm={12} xs={23} md={11} lg={12}>
                                <label className="required-label">State</label>
                                <Form.Item name="state" rules={[{ required: true, message: "State is required!" }]}>
                                    <SelectDropdown
                                        options={stateOptions}
                                        value={selectedState}
                                        onChange={handleStateChange}
                                        placeholder="Select State"
                                        isSearchable
                                    />
                                    {/* {touched.state && errors.state && <div className="createClientErrors">{errors.state}</div>} */}
                                </Form.Item>
                            </Col>
                            <Col sm={12} xs={23} md={11} lg={12}>
                                <label className="required-label">City</label>
                                <Form.Item name="city" rules={[{ required: true, message: "City is required!" }]}>
                                    <SelectDropdown
                                        options={cityOptions}
                                        value={selectedCity}
                                        onChange={handleCityChange}
                                        placeholder="Select City"
                                        isSearchable
                                        isDisabled={!selectedState}
                                    />
                                    {/* {touched.city && errors.city && <div className="createClientErrors">{errors.city}</div>} */}
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[16, 16]}>
                            <Col sm={12} xs={23} md={11} lg={12}>
                                <label className="required-label">Zone</label>

                                <Form.Item
                                    name="zone"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Zone is required!",
                                        },
                                    ]}
                                >
                                    <SelectDropdown
                                        options={[
                                            { label: "North", value: "north" },
                                            { label: "South", value: "south" },
                                            { label: "East", value: "east" },
                                            { label: "West", value: "west" },
                                        ]}
                                        onChange={handleZoneChange}
                                        placeholder="Zone"
                                        isSearchable
                                    />

                                    {/* {touched.zone && errors.zone && (
                                        <div className="createClientErrors">
                                            {errors.zone}
                                        </div>
                                    )} */}
                                </Form.Item>
                            </Col>
                            <Col sm={12} xs={23} md={11} lg={12}>
                                <label>Select Manager</label>
                                <Form.Item name="managerSelection">
                                    <SelectDropdown
                                        options={managerOptions}
                                        placeholder="Select Manager"
                                        onChange={handleManagerSelect}
                                        isSearchable
                                        isClearable
                                        isLoading={managersListLoading}
                                        onInputChange={(inputValue) => setManagerSearchTerm(inputValue)}
                                        value={selectedManagerValue}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col sm={12} xs={23} md={11} lg={12}>
                                <label className={selectedManagerValue ? "required-label" : ""}>Date of Reporting</label>
                                <Form.Item
                                    name="reportingDate"
                                    dependencies={["managerSelection"]}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                const managerId = getFieldValue("managerSelection");
                                                if (managerId && !value) {
                                                    return Promise.reject(new Error("Date of Reporting is required when Manager is selected!"));
                                                }
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        placeholder="Date of Reporting"
                                        value={editFormData.reportingDate}
                                        onChange={(date) => handleDateChange("reportingDate", date)}
                                        format="YYYY-MM-DD"
                                    />
                                    {/* {touched.reportingDate && errors.reportingDate && <div className="createClientErrors">{errors.reportingDate}</div>} */}
                                </Form.Item>
                            </Col>
                            <Col sm={12} xs={23} md={11} lg={12}>
                                <label>Employee ID of Manager</label>
                                <Form.Item>
                                    <TextInputField
                                        placeholder="Employee ID of Manager"
                                        value={selectedManagerEmployeeId || "No Manager Selected"}
                                        disabled
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col sm={12} xs={23} md={11} lg={12}>
                                <label className="required-label">Login Type</label>
                                <Form.Item name="userType" rules={[{ required: true, message: "Login Type is required!" }]}>
                                    <SelectDropdown
                                        options={userTypesOptions}
                                        value={editFormData.userType}
                                        onChange={(selectedOption) => handleSelectChange(selectedOption, "userType")}
                                        placeholder="Select Login Type"
                                        isSearchable
                                        isDisabled={userTypesLoading}
                                    />
                                    {/* {touched.userType && errors.userType && <div className="createClientErrors">{errors.userType}</div>} */}
                                </Form.Item>
                            </Col>
                        </Row>

                        <div style={{ display: "flex", justifyContent: "end", marginTop: "20px", gap: "10px" }}>
                            <button className="user-management-cancel-button" onClick={handleCancel}>Cancel</button>
                            <button className="user-management-save-button" onClick={handleSave} disabled={updateUserLoading} style={{ opacity: updateUserLoading ? 0.7 : 1, cursor: updateUserLoading ? 'not-allowed' : 'pointer' }}>{updateUserLoading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Save'}</button>
                        </div>
                    </>
                )}
            </Form>

        </Modal>
    );
};

export default EditUserModal;